import React from 'react';
import { Link, useLocation } from 'react-router-dom';  // ✅ Add useLocation
import { motion } from 'framer-motion';
import { FaLinkedin, FaInstagram, FaWhatsapp, FaGithub } from 'react-icons/fa';
import './Footer.css';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const location = useLocation();  // ✅ Get current location

  const footerLinks = {
    company: [
      { name: 'About Us', path: '/about' },
      { name: 'Our Team', path: '/team' },
      { name: 'Careers', path: '/careers' },
      { name: 'Blog', path: '/blog' }
    ],
    services: [
      { name: 'Web Development', path: '/services#web-development' },
      { name: 'Mobile Apps', path: '/services#mobile-apps' },
      { name: 'Cloud Solutions', path: '/services#cloud-solutions' },
      { name: 'AI/ML', path: '/services#ai-ml' }
    ],
    support: [
      { name: 'Contact Us', path: '/contact' },
      { name: 'FAQ', path: '/faq' },
      { name: 'Privacy Policy', path: '/privacy' },
      { name: 'Terms of Service', path: '/terms' }
    ]
  };

  const socialLinks = [
    { 
      icon: <FaLinkedin />, 
      url: 'https://www.linkedin.com/company/zustand-tech/', 
      color: '#0a66c2' 
    },
    { 
      icon: <FaInstagram />, 
      url: 'https://www.instagram.com/zustandtech?igsh=bm1qemF3em4xZ2Ux', 
      color: '#e4405f' 
    },
    {
      icon: <FaWhatsapp />,
      url: 'https://wa.me/919196054906',
      color: '#25D366'
    }
  ];

  // ✅ Handle same page scroll to top
  const handleFooterClick = (e, path) => {
    const currentPath = location.pathname;
    
    // If it's a hash link (services anchor)
    if (path.includes('#')) {
      e.preventDefault();
      const [route, hash] = path.split('#');
      
      // If already on the same page with hash
      if (currentPath === route) {
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      } else {
        // Navigate to different page with hash
        window.location.href = path;
      }
      return;
    }
    
    // ✅ If already on the same page, scroll to top
    if (currentPath === path) {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <footer className="footer">
      <div className="footer-wave">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 320">
          <path fill="var(--primary)" fillOpacity="1" d="M0,96L48,112C96,128,192,160,288,160C384,160,480,128,576,122.7C672,117,768,139,864,154.7C960,171,1056,181,1152,170.7C1248,160,1344,128,1392,112L1440,96L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path>
        </svg>
      </div>
      
      <div className="footer-content">
        <div className="container">
          <div className="footer-grid">
            <div className="footer-section brand-section">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="footer-logo"
              >
                <span className="logo-text">Zustand<span className="logo-highlight">Tech</span></span>
              </motion.div>
              <p className="footer-description">
                Transforming ideas into innovative software solutions. We build digital experiences that drive business growth.
              </p>
              <div className="social-links">
                {socialLinks.map((social, index) => (
                  <motion.a
                    key={index}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="social-link"
                    whileHover={{ y: -5, color: social.color }}
                    style={{ '--social-color': social.color }}
                  >
                    {social.icon}
                  </motion.a>
                ))}
              </div>
            </div>

            {Object.entries(footerLinks).map(([category, links], index) => (
              <div key={category} className="footer-section">
                <motion.h3
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="footer-title"
                >
                  {category.charAt(0).toUpperCase() + category.slice(1)}
                </motion.h3>
                <ul className="footer-links">
                  {links.map((link, linkIndex) => (
                    <motion.li
                      key={link.name}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: linkIndex * 0.1 }}
                    >
                      {link.path.includes('#') ? (
                        // ✅ Hash links (services anchors)
                        <a 
                          href={link.path}
                          onClick={(e) => handleFooterClick(e, link.path)}
                          className="footer-link"
                        >
                          {link.name}
                        </a>
                      ) : (
                        // ✅ Regular links with same-page scroll to top
                        <Link 
                          to={link.path} 
                          className="footer-link"
                          onClick={(e) => handleFooterClick(e, link.path)}
                        >
                          {link.name}
                        </Link>
                      )}
                    </motion.li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="footer-bottom">
            <div className="footer-bottom-content">
              <p className="copyright">
                &copy; {currentYear} Zustand Tech Software Private Limited (ZustandTech). All rights reserved.
              </p>
              <div className="footer-bottom-links">
                <Link to="/privacy" onClick={(e) => handleFooterClick(e, '/privacy')}>Privacy Policy</Link>
                <span className="separator">|</span>
                <Link to="/terms" onClick={(e) => handleFooterClick(e, '/terms')}>Terms of Service</Link>
                <span className="separator">|</span>
                <Link to="/sitemap" onClick={(e) => handleFooterClick(e, '/sitemap')}>Sitemap</Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;